import { observer } from "mobx-react";
import FontBody1 from "../../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../components/fonts/font-props";
import { FormFieldLine } from "../../../../../../components/form-field-line/form-field-line";
import { SelectDropdown } from "../../../../../../components/inputs/select-dropdown/select-dropdown";
import { Toggle } from "../../../../../../components/inputs/toggle/toggle";
import Card from "../../../../../../components/layouts/card/card";
import {
	INSTANT_REFUND_PROVIDER,
	INSTANT_REFUND_PROVIDER_IMAGE_MAP,
	RESOLUTION_ID,
	RESOLUTION_TYPE
} from "../../../../constants";
import resolutionListStore from "../../../../stores/resolution-list-store";
import { IResolutionTypeProps } from "../resolution-type-viewer-props";
import OfferRange from "./components/offer-range/offer-range";
import RefundAmount from "./components/refund-amount/refund-amount";
import RefundType from './components/refund-type/refund-type';
import VoucherType from "./components/voucher-type/voucher-type";
import './refund.scss';
import { IRefundProviderOption } from "../../../../../../services/api/resolutions-service";
import { Note } from "../../../../../../components/note/note";
import { PROFILE_CAPABILITIES } from "../../../../../../models/ProfileCapabilities";
import ResolveCapability from "../../../../../../components/resolve-capability/resolve-capability";


const Custom: React.FC<IResolutionTypeProps> = observer(({ resolution, validationGroup }) => {
    const RefundTypeComponent = resolution?.isReturnScore() ? VoucherType : RefundType;
    const ValueComponent = resolution?.isReturnScore() ? OfferRange : RefundAmount;

    const paymentRefundTypes = [RESOLUTION_TYPE.BankRefund, RESOLUTION_TYPE.Refund, RESOLUTION_TYPE.InstantRefundType];

    const instantRefundProviders = resolutionListStore.settings.refund_service_providers.map(provider => {
        return {
            value: provider.value,
            resolution_type: provider.resolution_type,
            label: (
                <div className="refund-provider-option">
                    <img src={INSTANT_REFUND_PROVIDER_IMAGE_MAP[provider.value as INSTANT_REFUND_PROVIDER]} width="16" height="16" alt=""/>
                    {provider.label}
                </div>
            )

        };
    });
    const selectedInstantRefundProvider = instantRefundProviders.find(p => p.value === resolution?.instantRefundProvider);

    const shouldShowIntegrations = resolution?.resolutionType === RESOLUTION_TYPE.InstantRefundType || resolution?.instantRefundProvider !== null;

    const isBankRefundResolution = resolution?.resolutionId === RESOLUTION_ID.REFUND_TO_BANK;

    return (
        <Card
            toolbar={{
                title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Refund</FontBody1>,
            }}
        >
            <FormFieldLine
                label="Refund Type"
            >
                <RefundTypeComponent resolution={resolution} validationGroup={validationGroup} />
            </FormFieldLine>

            {isBankRefundResolution && <Note body="Bank refund resolutions will allow you to collect banking information from your shopper and will not issue the bank refund. This resolution is only allowed for order that have been paid in cash." />}

            {shouldShowIntegrations &&
                <FormFieldLine
                    label="Integration"
                >
                    <SelectDropdown
                        value={selectedInstantRefundProvider}
                        options={instantRefundProviders}
                        onChange={(v) => resolution?.setInstantRefundProvider(v as IRefundProviderOption)}
                        required
                        validationGroup={validationGroup} />
                </FormFieldLine>
            }

			<ResolveCapability capability={PROFILE_CAPABILITIES.REFUND_RESOLUTION}>
				{({ isRefundAmountSettingSupported, isAutomaticPaymentGatewayDetectionSupported }) => (
					<>
						{(isRefundAmountSettingSupported || resolution?.isReturnScore()) && (
							<FormFieldLine
								label="Refund Amount"
								note="Set the amount you want to offer the customer, relative to the paid price."
							>
								<ValueComponent resolution={resolution} validationGroup={validationGroup} />
							</FormFieldLine>
						)}

						{isAutomaticPaymentGatewayDetectionSupported && paymentRefundTypes.includes(resolution?.resolutionType as RESOLUTION_TYPE) &&
							<FormFieldLine
								label="Automatic Payment Gateway Detection"
								note={
									<>
										For Bank Refund: show only for orders paid with cash <br />
										For Refund to Payment Method: hide for orders paid with cash <br />
										For specific payment gateways contact&nbsp;
										<a href="mailto:support@returngo.ai">support@returngo.ai</a>
									</>
								}
							>
								<Toggle
									checked={!!resolution?.excludeByGateway}
									label=""
									onChange={(v) => resolution?.setExcludeByGateway(v)}
								/>
							</FormFieldLine>
						}
					</>
				)}
			</ResolveCapability>
        </Card>
    )
});

export default Custom;